import { useDialog } from "helper/customHook";
import ViewTable from "modules/DeclareLeave/components/CustomTable";
import { selectListRequestLeaveManager } from "modules/DeclareLeave/redux/selector";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import pxToRem from "theme/base/pxToRem";
import {
  ApproveStatus,
  ApproveStatusValue,
  statusColor,
} from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/const";

import { DIALOG } from "helper/enum/dialogEnum";
import EmptyState from "components/common/EmptyState";
import { PopupConfirm } from "./PopupConfirm";
import pathRoutes from "routes/pathRoutes";
import { useNavigate, useParams } from "react-router-dom";
import LeaveRequestDetailManager from "modules/DeclareLeave/components/CustomCalendarTimeKeeping/components/Modals/LeaveRequestManagerDetail";
import { setupDataRequest } from "modules/DeclareLeave/redux/handleRequest";
import { formatDateStringToDDMMYYYY } from "helper/utils";
import { isNumber } from "lodash";
import { declareLeaveURL } from "modules/DeclareLeave/Pages/Main/DeclareLeaveContent/index";
import { memo } from "react";

export const ListLeaveRequest = memo(() => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const data = useSelector(selectListRequestLeaveManager);
  const approveDialog = useDialog(DIALOG.APPROVE_REQUEST_POPUP);
  const requestLeaveDialog = useDialog(DIALOG.VIEW_REQUEST_LEAVE_DETAIL);

  const handleApprove = (
    e,
    isApprove: boolean,
    id: string,
    warning: string | null
  ) => {
    dispatch(setupDataRequest({ isApprove, id, warning }));

    e.stopPropagation();
    approveDialog.open();
  };
  const columns = [
    {
      id: "registerDate",
      accessorKey: "registerDate",
      header: "Ngày tạo đơn",
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {formatDateStringToDDMMYYYY(info.getValue())}
        </Typography>
      ),
      minSize: 120,
      pinPosition: "left",
    },
    {
      id: "employeeName",
      accessorKey: "employeeName",
      header: "Tên nhân viên",
      minSize: 115,
      pinPosition: "left",
    },
    {
      id: "positionName",
      accessorKey: "positionName",
      header: "Chức danh",
      minSize: 110,
      pinPosition: "left",
    },
    {
      id: "departmentName",
      accessorKey: "departmentName",
      header: "Đơn vị",
      footer: (props) => props.column.id,
      minSize: 110,
      pinPosition: "left",
    },
    {
      id: "type",
      accessorKey: "type",
      header: "Loại",

      cell: (info) => {
        const listType = (info.getValue() ?? "").split("#") ?? [];
        return (
          <Box display={"flex"} flexDirection={"column"}>
            {listType.map((type, index) => (
              <Typography
                key={index}
                sx={(theme) => ({
                  fontSize: pxToRem(12),
                  color: theme.palette.text.textPrimary,
                })}
              >
                {type}
              </Typography>
            ))}
          </Box>
        );
      },
      footer: (props) => props.column.id,
      minSize: 120,
      enablePinning: true,
    },
    {
      accessorKey: "dayOffs",
      header: () => "Ngày nghỉ",
      footer: (props) => props.column.id,
      cell: (info) => {
        const dayOffs = (info.getValue() ?? "").split("#") ?? [];
        return (
          <Box display={"flex"} flexDirection={"column"}>
            {dayOffs.map((type, index) => (
              <Typography
                key={index}
                sx={(theme) => ({
                  fontSize: pxToRem(12),
                  color: theme.palette.text.textPrimary,
                })}
              >
                {type}
              </Typography>
            ))}
          </Box>
        );
      },
      minSize: 250,
    },
    {
      accessorKey: "totalLeaveDay",
      header: () => <span>Số ngày nghỉ</span>,
      footer: (props) => props.column.id,
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {isNumber(info.getValue()) &&
          info.getValue() < 10 &&
          info.getValue() >= 1
            ? `0${info.getValue()}`
            : info.getValue()}
        </Typography>
      ),
      minSize: 120,
    },
    {
      accessorKey: "leaveReason",
      header: "Lý do nghỉ",
      cell: (info) => {
        const reasons = (info.getValue() ?? "").split("#") ?? [];
        return (
          <Box display={"flex"} flexDirection={"column"}>
            {reasons.map((type, index) => (
              <Typography
                key={index}
                sx={(theme) => ({
                  fontSize: pxToRem(12),
                  color: theme.palette.text.textPrimary,
                })}
              >
                {type}
              </Typography>
            ))}
          </Box>
        );
      },
      footer: (props) => props.column.id,
      minSize: 180,
    },
    {
      id: "modifiedDate",
      accessorKey: "modifiedDate",
      header: "Ngày cập nhật đơn",
      cell: (info) => (
        <Typography
          sx={(theme) => ({
            fontSize: pxToRem(12),
            color: theme.palette.text.textPrimary,
            width: "100%",
            textAlign: "center",
          })}
        >
          {info.getValue() ? formatDateStringToDDMMYYYY(info.getValue()) : ""}
        </Typography>
      ),
      footer: (props) => props.column.id,
      minSize: 155,
      pinPosition: "left",
    },
    {
      accessorKey: "status",
      header: "Trạng thái",
      footer: (props) => props.column.id,
      cell: (info) => {
        const status = info.getValue();
        return (
          <Typography
            sx={(theme) => ({
              fontWeight: 600,
              fontSize: pxToRem(12),
              color: statusColor(theme, status, "color"),
            })}
          >
            {ApproveStatus[status]}
          </Typography>
        );
      },
      minSize: 100,
    },
    {
      accessorKey: "action",
      header: "",

      cell: (info) => (
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={1}
          sx={{
            background: "white",
            marginLeft: `-${pxToRem(15)}`,
            marginRight: `-${pxToRem(17)}`,
            width: `calc(100% + ${pxToRem(32)})`,
            height: `100%`,
            justifyContent: "center",
            padding: `${pxToRem(8)} ${pxToRem(16)}`,
          }}
        >
          {info.row.original.status === ApproveStatusValue.PENDING ? (
            <>
              <Button
                onClick={(e) =>
                  handleApprove(
                    e,
                    true,
                    info.row.original.id,
                    info.row.original.warning
                  )
                }
                color="success"
                sx={{
                  fontSize: pxToRem(12),
                  padding: `${pxToRem(4)} ${pxToRem(10)}`,
                }}
              >
                Duyệt
              </Button>
              <Button
                sx={{
                  fontSize: pxToRem(12),
                  padding: `${pxToRem(4)} ${pxToRem(10)}`,
                }}
                onClick={(e) =>
                  handleApprove(
                    e,
                    false,
                    info.row.original.id,
                    info.row.original.warning
                  )
                }
                color="error"
              >
                Từ chối
              </Button>
            </>
          ) : (
            <></>
          )}
        </Box>
      ),
      minSize: 100,
    },
  ];

  const handleViewDetail = (idRecord: string) => {
    if (idRecord === id) {
      requestLeaveDialog.open();
    } else {
      navigate(
        `${pathRoutes.declareLeave.base}/${declareLeaveURL.orderManagement}/${declareLeaveURL.leave}/${idRecord}`,
        {
          replace: true,
        }
      );
    }
  };

  return (
    <>
      <ViewTable
        data={data}
        columns={columns}
        columnPinning={{
          left: [],
          right: ["action"],
        }}
        handleFetchMore={() => {}}
        messageEmpty={
          <EmptyState
            message="Chưa có đơn xin nghỉ nào"
            sx={(theme) => ({
              h6: {
                color: theme.palette.text.textSecondary,
              },
            })}
          />
        }
        handleOnClickedRow={(item) => handleViewDetail(item.id)}
        height={"auto"}
      />
      <LeaveRequestDetailManager />
      <PopupConfirm />
    </>
  );
});
